import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { RequireRole } from "../../auth/RequireRole";
import { ApiError, describeError } from "../../api/client";
import type { AdminTemplateSummary } from "../../api/types";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { EmptyState } from "../../components/EmptyState";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { Pagination } from "../../components/Pagination";
import { SearchField } from "../../components/SearchField";
import { SegmentedControl } from "../../components/SegmentedControl";
import { StatusPill } from "../../components/StatusPill";
import { useUnsavedChangesBlocker } from "../../state/routeState";
import { listTemplates, replaceTemplateContent, softDeleteTemplate } from "./api";
import { templateCardBandColor, templateCardInitials } from "./derived";
import { resolveTemplateStatus } from "./status";
import { TemplateAttachmentsPanel } from "./TemplateAttachmentsPanel";
import { TemplateCreateForm } from "./TemplateCreateForm";
import { TemplatePreviewModal } from "./TemplatePreviewModal";
import { TemplateReplaceEditor } from "./TemplateReplaceEditor";

type ListState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; items: AdminTemplateSummary[] };

type TemplateFilter = "activas" | "inactivas" | "todas";

/**
 * Round 3: `GET panel/templates` returns the account's WHOLE template list (no server paging,
 * no `q`), and the status tabs already filter it client-side — so search and pagination are
 * client-side too: one fetch, no extra round trips, instant feedback, consistent counts.
 */
const TEMPLATES_PAGE_SIZE = 12;

function matchesSearch(item: AdminTemplateSummary, needle: string): boolean {
  if (!needle) return true;
  return [item.nombre, item.producto, item.temporada].some((field) => field.toLowerCase().includes(needle));
}

const FILTER_OPTIONS = [
  { value: "activas", label: "Activas" },
  { value: "inactivas", label: "Inactivas" },
  { value: "todas", label: "Todas" },
];

/**
 * `Desactivar` confirmation (REDISENO-PANEL.md §8.1: "Desactivar plantilla
 * (rojo, solo si está activa; misma lógica de soft-delete y mismo texto de
 * confirmación adaptado)" — same soft-delete `DELETE panel/templates/{id}`
 * call as before, renamed copy only.
 */
function buildSoftDeleteConfirmation(templateName: string): string {
  return `¿Desactivar la plantilla "${templateName}"? No podrá usarse en campañas nuevas.`;
}

/** One row's soft-delete lifecycle — absent from the map when idle, keyed by `templateId` so concurrent deactivations on different cards track independently (Codex PR #150's own fix, preserved). */
type SoftDeleteRowState = { status: "pending" } | { status: "error"; message: string };

/** One template's replace-content lifecycle — absent from the map when idle, keyed by `templateId` (Codex PR #152's own fix, preserved). */
type ReplaceRowState = { status: "pending" } | { status: "error"; message: string };

/**
 * `TemplatesScreen.tsx` (`/plantillas`, REDISENO-PANEL.md §8.1,
 * panel-redesign-v3 T8). Full rewrite from the pre-redesign
 * DataTable/RecordList row list onto the v3 card grid: header
 * ("`N` activas de `M`" + "+ Nueva plantilla"), an Activas/Inactivas/Todas
 * `SegmentedControl` filtering the already-fetched list client-side, and a
 * responsive card grid (`minmax(min(100%,250px),1fr)`) — one card per
 * template with a hash-colored band, name + `StatusPill`, "producto ·
 * temporada · versión N", and a footer with "Vista previa" (active only) or
 * "No se puede usar en campañas nuevas", plus a "⋯" menu (Ver detalle,
 * Adjuntos, Reemplazar contenido, red "Desactivar plantilla" when active).
 *
 * Every create/replace/soft-delete/attachments orchestration below (pending
 * state, typed-input preservation on error, per-row keyed state, the
 * `useUnsavedChangesBlocker` guard on "Ver detalle") is UNCHANGED from the
 * pre-redesign screen — only the list's own presentation is new here.
 */
export function TemplatesScreen() {
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<TemplateFilter>("activas");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [listState, setListState] = useState<ListState>({ status: "loading" });
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const [replaceHtml, setReplaceHtml] = useState("");
  const [replaceState, setReplaceState] = useState<Record<string, ReplaceRowState>>({});
  // Codex automated review on PR #148 (finding #3): a "Ver detalle" click
  // used to silently unmount this screen (discarding an unsaved Liquid
  // replacement draft) once it became a real route navigation. Reuses the
  // existing `state/routeState.ts` mechanism.
  const { confirmLeave } = useUnsavedChangesBlocker(replacingId !== null && replaceHtml.length > 0);
  const [softDeleteState, setSoftDeleteState] = useState<Record<string, SoftDeleteRowState>>({});
  const [pendingSoftDelete, setPendingSoftDelete] = useState<{ templateId: string; templateName: string } | null>(
    null,
  );
  const [attachmentsTemplateId, setAttachmentsTemplateId] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    // Only blank to the loading state on the FIRST fetch — a post-mutation
    // refresh with an already-`ready` list keeps showing that stale data
    // while the new one loads underneath (CLS discipline, unchanged).
    setListState((prev) => (prev.status === "ready" ? prev : { status: "loading" }));
    try {
      const items = await listTemplates();
      setListState({ status: "ready", items });
    } catch (error) {
      setListState({ status: "error", message: describeError(error, "No se pudo cargar el listado de plantillas.") });
    }
  }, []);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const openReplace = useCallback((templateId: string) => {
    setReplacingId(templateId);
    setReplaceHtml("");
  }, []);

  const cancelReplace = useCallback(() => {
    setReplacingId(null);
    setReplaceHtml("");
  }, []);

  const handleReplaceSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!replacingId) return;
      const templateId = replacingId;
      const html = replaceHtml;
      if (replaceState[templateId]?.status === "pending") return;
      setReplaceState((prev) => ({ ...prev, [templateId]: { status: "pending" } }));
      try {
        await replaceTemplateContent(templateId, { html });
        setReplaceState((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => id !== templateId)));
        let stillOnThisRow = false;
        setReplacingId((current) => {
          stillOnThisRow = current === templateId;
          return stillOnThisRow ? null : current;
        });
        if (stillOnThisRow) {
          setReplaceHtml("");
        }
        await fetchList();
      } catch (error) {
        setReplaceState((prev) => ({
          ...prev,
          [templateId]: {
            status: "error",
            message: error instanceof ApiError ? error.message : "No se pudo reemplazar el contenido.",
          },
        }));
      }
    },
    [replacingId, replaceHtml, replaceState, fetchList],
  );

  const performSoftDelete = useCallback(
    async (templateId: string) => {
      setSoftDeleteState((prev) => ({ ...prev, [templateId]: { status: "pending" } }));
      try {
        await softDeleteTemplate(templateId);
        setSoftDeleteState((prev) =>
          Object.fromEntries(Object.entries(prev).filter(([id]) => id !== templateId)),
        );
        await fetchList();
      } catch (error) {
        setSoftDeleteState((prev) => ({
          ...prev,
          [templateId]: {
            status: "error",
            message: error instanceof ApiError ? error.message : "No se pudo desactivar la plantilla.",
          },
        }));
      }
    },
    [fetchList],
  );

  const requestSoftDelete = useCallback(
    (templateId: string, templateName: string) => {
      if (softDeleteState[templateId]?.status === "pending") return;
      setPendingSoftDelete({ templateId, templateName });
    },
    [softDeleteState],
  );

  const confirmSoftDelete = useCallback(() => {
    if (!pendingSoftDelete) return;
    const { templateId } = pendingSoftDelete;
    setPendingSoftDelete(null);
    void performSoftDelete(templateId);
  }, [pendingSoftDelete, performSoftDelete]);

  const cancelSoftDelete = useCallback(() => {
    setPendingSoftDelete(null);
  }, []);

  const openAttachments = useCallback((templateId: string) => {
    setAttachmentsTemplateId(templateId);
  }, []);

  const closeAttachments = useCallback(() => {
    setAttachmentsTemplateId(null);
  }, []);

  const allItems = listState.status === "ready" ? listState.items : [];
  const activeCount = allItems.filter((item) => item.activo).length;
  const needle = search.trim().toLowerCase();
  const filteredItems = allItems.filter((item) => {
    if (filter === "activas" && !item.activo) return false;
    if (filter === "inactivas" && item.activo) return false;
    return matchesSearch(item, needle);
  });
  // The list shrinks after a deactivation/refetch: never sit on a page that no longer exists.
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / TEMPLATES_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleItems = filteredItems.slice((currentPage - 1) * TEMPLATES_PAGE_SIZE, currentPage * TEMPLATES_PAGE_SIZE);

  const replacingTemplate =
    replacingId && listState.status === "ready" ? listState.items.find((item) => item.templateId === replacingId) : undefined;
  const previewingTemplate =
    previewingId && listState.status === "ready" ? listState.items.find((item) => item.templateId === previewingId) : undefined;

  return (
    <section aria-label="Plantillas" className="v3-templates">
      <header className="v3-templates__header">
        <div>
          <h1>Plantillas</h1>
          {listState.status === "ready" && (
            <p className="v3-templates__subtitle">
              {activeCount} activas de {allItems.length}
            </p>
          )}
        </div>
        <RequireRole role="Operador">
          <button
            type="button"
            className="v3-templates__create-trigger v2-u-target-min v2-btn-primary"
            onClick={() => setCreating(true)}
          >
            + Nueva plantilla
          </button>
        </RequireRole>
      </header>

      <div className="v3-templates__toolbar">
        <SearchField
          id="templates-q"
          label="Buscar plantillas"
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
        />
        <SegmentedControl
          ariaLabel="Filtrar plantillas"
          options={FILTER_OPTIONS}
          value={filter}
          onChange={(value) => {
            setFilter(value as TemplateFilter);
            setPage(1);
          }}
        />
      </div>

      {creating && (
        <TemplateCreateForm
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void fetchList();
          }}
        />
      )}

      <TemplatesGrid
        state={listState}
        items={visibleItems}
        filter={filter}
        search={search.trim()}
        confirmLeave={confirmLeave}
        softDeleteState={softDeleteState}
        onRetry={() => void fetchList()}
        onPreview={setPreviewingId}
        onStartReplace={openReplace}
        onSoftDelete={requestSoftDelete}
        onOpenAttachments={openAttachments}
      />

      {listState.status === "ready" && filteredItems.length > TEMPLATES_PAGE_SIZE && (
        <Pagination
          variant="range"
          page={currentPage}
          size={TEMPLATES_PAGE_SIZE}
          total={filteredItems.length}
          onPageChange={setPage}
          onSizeChange={() => undefined}
        />
      )}

      {previewingTemplate && (
        <TemplatePreviewModal
          templateId={previewingTemplate.templateId}
          templateName={previewingTemplate.nombre}
          onClose={() => setPreviewingId(null)}
        />
      )}

      {pendingSoftDelete && (
        <ConfirmDialog
          message={buildSoftDeleteConfirmation(pendingSoftDelete.templateName)}
          confirmLabel="Desactivar"
          onConfirm={confirmSoftDelete}
          onCancel={cancelSoftDelete}
        />
      )}

      {replacingId && (
        <TemplateReplaceEditor
          templateId={replacingId}
          templateName={replacingTemplate?.nombre}
          currentVersion={replacingTemplate?.version}
          html={replaceHtml}
          error={replaceState[replacingId]?.status === "error" ? replaceState[replacingId].message : null}
          pending={replaceState[replacingId]?.status === "pending"}
          onHtmlChange={setReplaceHtml}
          onSubmit={(event) => void handleReplaceSubmit(event)}
          onCancel={cancelReplace}
        />
      )}

      {attachmentsTemplateId && (
        <TemplateAttachmentsPanel templateId={attachmentsTemplateId} onClose={closeAttachments} />
      )}
    </section>
  );
}

const FILTER_LABELS: Record<TemplateFilter, string> = {
  activas: "Activas",
  inactivas: "Inactivas",
  todas: "Todas",
};

function TemplatesGrid({
  state,
  items,
  filter,
  search,
  confirmLeave,
  softDeleteState,
  onRetry,
  onPreview,
  onStartReplace,
  onSoftDelete,
  onOpenAttachments,
}: {
  state: ListState;
  items: AdminTemplateSummary[];
  filter: TemplateFilter;
  search: string;
  confirmLeave: () => boolean;
  softDeleteState: Record<string, SoftDeleteRowState>;
  onRetry: () => void;
  onPreview: (id: string) => void;
  onStartReplace: (id: string) => void;
  onSoftDelete: (id: string, name: string) => void;
  onOpenAttachments: (id: string) => void;
}) {
  if (state.status === "loading") {
    return <p role="status">Cargando plantillas…</p>;
  }

  if (state.status === "error") {
    return <FeedbackPanel variant="error" message={state.message} onRetry={onRetry} />;
  }

  if (state.items.length === 0) {
    return <EmptyState title="No hay plantillas aquí" description="Crea una plantilla pegando el HTML del correo." />;
  }

  if (items.length === 0 && search) {
    return (
      <EmptyState
        title={`No hay plantillas que coincidan con «${search}».`}
        description="Prueba con otro nombre, producto o temporada."
      />
    );
  }

  if (items.length === 0) {
    return <EmptyState title={`No hay plantillas en "${FILTER_LABELS[filter]}"`} description="Prueba con otro filtro." />;
  }

  return (
    <ul aria-label="Plantillas" className="v3-templates-grid">
      {items.map((template) => (
        <TemplateCard
          key={template.templateId}
          template={template}
          confirmLeave={confirmLeave}
          softDeleteState={softDeleteState[template.templateId]}
          onPreview={onPreview}
          onStartReplace={onStartReplace}
          onSoftDelete={onSoftDelete}
          onOpenAttachments={onOpenAttachments}
        />
      ))}
    </ul>
  );
}

function TemplateCard({
  template,
  confirmLeave,
  softDeleteState,
  onPreview,
  onStartReplace,
  onSoftDelete,
  onOpenAttachments,
}: {
  template: AdminTemplateSummary;
  confirmLeave: () => boolean;
  softDeleteState: SoftDeleteRowState | undefined;
  onPreview: (id: string) => void;
  onStartReplace: (id: string) => void;
  onSoftDelete: (id: string, name: string) => void;
  onOpenAttachments: (id: string) => void;
}) {
  const status = resolveTemplateStatus(template.activo);
  const isDeleting = softDeleteState?.status === "pending";
  const deleteError = softDeleteState?.status === "error" ? softDeleteState.message : null;

  return (
    <li className="v3-template-card">
      <div
        className={`v3-template-card__band${template.activo ? "" : " v3-template-card__band--inactive"}`}
        style={{ background: templateCardBandColor(template.templateId, template.activo) }}
      >
        <span className="v3-template-card__band-initials">{templateCardInitials(template.producto)}</span>
        <span className="v3-template-card__band-version">v{template.version}</span>
      </div>
      <div className="v3-template-card__body">
        <div className="v3-template-card__title-row">
          <Link
            to={`/plantillas/${template.templateId}`}
            onClick={(event) => {
              if (!confirmLeave()) {
                event.preventDefault();
              }
            }}
          >
            {template.nombre}
          </Link>
          <StatusPill label={status.label} tone={status.tone} />
        </div>
        <p className="v3-template-card__meta">
          {template.producto} · {template.temporada} · versión {template.version}
        </p>
      </div>
      <div className="v3-template-card__footer">
        {template.activo ? (
          <button type="button" className="v2-u-target-min" onClick={() => onPreview(template.templateId)}>
            Vista previa
          </button>
        ) : (
          <span className="v3-template-card__unavailable">No se puede usar en campañas nuevas</span>
        )}
        <TemplateCardMenu templateName={template.nombre}>
          <Link
            className="v2-data-table__action-link"
            to={`/plantillas/${template.templateId}`}
            onClick={(event) => {
              if (!confirmLeave()) {
                event.preventDefault();
              }
            }}
          >
            Ver detalle
          </Link>
          <button type="button" className="v2-u-target-min" onClick={() => onOpenAttachments(template.templateId)}>
            Adjuntos
          </button>
          <RequireRole role="Operador">
            <button type="button" className="v2-u-target-min" onClick={() => onStartReplace(template.templateId)}>
              Reemplazar contenido
            </button>
            {template.activo && (
              <button
                type="button"
                className="v2-u-target-min v2-u-danger"
                onClick={() => onSoftDelete(template.templateId, template.nombre)}
                disabled={isDeleting}
                aria-busy={isDeleting}
              >
                Desactivar plantilla
              </button>
            )}
          </RequireRole>
        </TemplateCardMenu>
      </div>
      {isDeleting && <p role="status">Desactivando…</p>}
      {deleteError && <p role="alert">{deleteError}</p>}
    </li>
  );
}

/**
 * The card's "⋯" action menu — same floating `position: fixed` mechanism
 * `TemplateRowActionsMenu` established for the pre-redesign table rows,
 * generalized to a card footer trigger instead of a table cell (the
 * anchoring math is identical: computed from the trigger's own
 * `getBoundingClientRect()`, right-aligned so it never runs off a narrow
 * card's edge).
 */
function TemplateCardMenu({ children, templateName }: { children: ReactNode; templateName: string }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function closeIfOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <span className="v2-template-row-actions">
      <button
        type="button"
        ref={triggerRef}
        className="v2-u-target-min v2-template-row-actions__trigger"
        aria-label={`Más acciones de ${templateName}`}
        aria-expanded={open}
        onClick={() => {
          if (!open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
          }
          setOpen((prev) => !prev);
        }}
      >
        ⋯
      </button>
      {open && position && (
        <div ref={menuRef} className="v2-template-row-actions__menu" style={{ top: position.top, right: position.right }}>
          {children}
        </div>
      )}
    </span>
  );
}
